<script lang="ts">
  import { addCredits } from '../lib/components/map/credits';
  import { onMount, onDestroy } from 'svelte';
  import { navigate } from 'svelte-routing';
  import * as maplibregl from 'maplibre-gl';
  import { api } from '../lib/api/client';
  import type { CheckIn, Fix, LiveJoined, MapInfo } from '../lib/api/types';
  import { isAuthenticated } from '../lib/stores/auth';
  import { websocket } from '../lib/stores/websocket';
  import { age, when } from '../lib/utils/time';
  import { initials } from '../lib/utils/geo';
  import { wakeCause } from '../lib/utils/memberState';
  import { watchSight } from '../lib/utils/sight';
  import { followTheme, northUpMap, styleFor } from '../lib/components/map/theme';

  import { clockAt } from '../lib/utils/checkin';
  import { integer, t } from '../lib/i18n/index.ts';

  // CheckInAlert shows a check-in timer to a person it names (004 FR-033,
  // 004 FR-029): while it runs, when it is due; once it ran out, where the
  // member last was and when, a way to call them, and live following, which
  // starts on its own, as a timer alert does. An alert nobody resolved reads
  // as closed a day after it went out, and is no longer followed.
  let { id }: { id: string } = $props();

  let timer: CheckIn | null = $state(null);
  let error = $state('');
  let liveState = $state('');
  // liveReason says why the phone could not be reached, when the server knows.
  let liveReason = $state('');
  let mapEl: HTMLDivElement | undefined = $state();
  let map: maplibregl.Map | null = null;
  let stopTheme: (() => void) | null = null;
  let marker: maplibregl.Marker | null = null;
  // located is whether the map shows the member: an alert may have no
  // location until following brings one (004 FR-033).
  let located = $state(false);
  let session = '';
  let unsubscribe: (() => void) | null = null;
  let rejoin: ReturnType<typeof setInterval> | null = null;
  // centering keeps the member in the middle of the map until the viewer
  // drags it (002 FR-017).
  let centering = true;
  // A page out of sight for 2 minutes stops following, and follows again
  // when it comes back while the alert still stands (002 FR-015).
  const stopWatchingSight = watchSight(
    () => {
      unfollow();
      liveState = 'away';
    },
    () => {
      if (timer?.group_id && timer.state === 'alerted') follow(timer.group_id, timer.user_id);
    },
  );

  $effect(() => {
    if (!$isAuthenticated) navigate('/login?next=/check-in/' + id, { replace: true });
  });

  onMount(async () => {
    try {
      timer = await api.get<CheckIn>(`/check-ins/${id}`);
    } catch (e: any) {
      error = e.message;
      return;
    }
    info = await api.get<MapInfo>('/map').catch(() => null);
    if (timer.lat != null && timer.lon != null) show([timer.lon, timer.lat]);
    // An alert without a location is still followed; the map appears with
    // the first fix live following brings (004 FR-033).
    if (timer.group_id && timer.state === 'alerted') follow(timer.group_id, timer.user_id);
  });

  let info: MapInfo | null = null;

  // show moves the member's marker, making the map on the first location.
  function show(at: [number, number]) {
    if (marker) {
      marker.setLngLat(at);
      if (centering) map?.easeTo({ center: at, duration: 500 });
      return;
    }
    if (!mapEl || !timer) return;
    map = northUpMap({ container: mapEl, style: styleFor(info), center: at, zoom: 15, attributionControl: false });
    // The fallback background has no sources to carry the attribution.
    addCredits(map, info, 'other');
    const el = document.createElement('div');
    el.className = 'checkin-marker';
    el.textContent = initials(timer.display_name);
    marker = new maplibregl.Marker({ element: el }).setLngLat(at).addTo(map);
    located = true;
    stopTheme = followTheme(map, info, () => {});
    map.on('dragstart', (e) => {
      if (e.originalEvent) centering = false;
    });
  }

  onDestroy(() => {
    stopTheme?.();
    stopWatchingSight();
    unfollow();
    map?.remove();
  });

  // unfollow leaves the live session.
  // generation counts unfollows, so a join still waiting on the server when
  // following stops does not start it again.
  let generation = 0;

  function unfollow(): Promise<void> {
    generation++;
    unsubscribe?.();
    unsubscribe = null;
    if (rejoin) clearInterval(rejoin);
    rejoin = null;
    const left = session ? api.delete(`/live/${session}/viewers/me`).then(() => {}, () => {}) : Promise.resolve();
    session = '';
    return left;
  }

  async function follow(groupId: string, userId: string) {
    try {
      await join(groupId, userId);
      rejoin = setInterval(() => join(groupId, userId).catch(() => {}), 60_000);
    } catch {
      liveState = '';
    }
  }

  // join joins the member's live session and subscribes to it. It runs again
  // every minute and after the socket reconnects: a reconnect counts as
  // leaving, so the socket's own subscription is refused until the viewer
  // has joined again (002 FR-014, 002 FR-015).
  async function join(groupId: string, userId: string) {
    const gen = generation;
    const j = await api.post<LiveJoined>(`/groups/${groupId}/members/${userId}/live`);
    if (gen !== generation) return;
    if (!liveState || j.session_id !== session) {
      liveState = j.state;
      liveReason = j.reason ?? '';
    }
    session = j.session_id;
    unsubscribe?.();
    unsubscribe = websocket.subscribeTopic(j.topic, (payload, _t, type) => {
      if (type === 'point') {
        const f = payload as Fix;
        show([f.lon, f.lat]);
        liveState = 'streaming';
      } else if (type === 'state') {
        const st = payload as { state: string; reason?: string };
        liveState = st.state;
        liveReason = st.reason ?? '';
        // An ended session, as after "I'm OK", is not joined again, which
        // would wake the member's phone; the timer is read afresh instead.
        if (st.state === 'ended') {
          unfollow();
          liveState = 'ended';
          api.get<CheckIn>(`/check-ins/${id}`).then((c) => (timer = c)).catch(() => {});
        }
      }
    });
  }

  let wasConnected = false;
  $effect(() => {
    const connected = $websocket.connected;
    if (connected && !wasConnected && session && timer?.group_id) join(timer.group_id, timer.user_id).catch(() => {});
    wasConnected = connected;
  });

  // tryAgain leaves and joins again, which wakes the phone again, as the
  // map's Follow again does (spec 012).
  async function tryAgain() {
    if (!timer?.group_id) return;
    // Leaving must reach the server before joining again, or the join finds
    // the viewer still in and wakes nothing.
    await unfollow();
    follow(timer.group_id, timer.user_id);
  }

  // liveWords says how following goes; a state the page does not know shows
  // as the server named it.
  function liveWords(state: string, reason: string, shown: boolean): string {
    switch (state) {
      case 'waiting': return t('checkinLiveWaiting');
      case 'streaming': return t('checkinLiveStreaming');
      case 'unreachable': {
        const located = shown ? 'yes' : 'no';
        return reason ? t('checkinLiveUnreachableBecause', { cause: wakeCause(reason), located }) : t('checkinLiveUnreachable', { located });
      }
      case 'ended': return t('checkinLiveEnded');
      case 'away': return t('checkinLiveAway');
      default: return state;
    }
  }
</script>

<div class="flex flex-1 min-h-0">
  <aside class="w-96 shrink-0 border-r p-5 space-y-3" style="border-color: var(--theme-surface0); background: var(--theme-mantle);">
    {#if error}
      <p style="color: var(--theme-error);">{error}</p>
    {:else if timer}
      <h1 class="text-xl font-semibold">
        {#if timer.state === 'running'}{t('checkinAlertDue', { member: timer.display_name, time: clockAt(timer.due_at) })}
        {:else if timer.state === 'alerted'}{t('checkinAlertLate', { member: timer.display_name })}
        {:else if timer.state === 'closed'}{t('checkinAlertClosed', { member: timer.display_name })}
        {:else}{t('checkinAlertDone', { member: timer.display_name })}{/if}
      </h1>
      {#if timer.note}<p>{timer.note}</p>{/if}
      {#if timer.watch}
        <!-- What the place was to show, so the alert says what was expected (spec 003 FR-049).
             The spot where the member started has no name. -->
        <p>{!timer.watch_name
          ? t('checkinAlertExpectedStart', { watch: timer.watch })
          : t('checkinAlertExpected', { watch: timer.watch, place: timer.watch_name })}</p>
      {/if}
      {#if timer.state === 'running'}
        <p class="text-sm text-text-muted">{t('checkinAlertRunning')}</p>
      {:else}
        <p class="text-sm">{timer.late ? t('checkinAlertDueWhenLate', { when: when(timer.due_at) }) : t('checkinAlertDueWhen', { when: when(timer.due_at) })}</p>
        {#if timer.located_at}
          <p class="text-sm">{t('checkinAlertLast', { age: age(timer.located_at), accuracy: integer(Math.round(timer.accuracy_m ?? 0)) })}</p>
        {:else if timer.state === 'alerted' && !located}
          <p class="text-sm">{t('checkinAlertUnknown')}</p>
        {/if}
        {#if timer.state === 'closed'}
          <p class="text-sm text-text-muted">{t('checkinAlertClosedNote', { member: timer.display_name })}</p>
        {/if}
      {/if}
      {#if timer.state === 'running'}
        <!-- Nothing more until it runs out. -->
      {:else if timer.phone}
        <a class="btn btn-primary inline-block" href={`tel:${timer.phone}`}><i aria-hidden="true" class="las la-phone mr-1"></i>{t('checkinAlertCall', { member: timer.display_name, phone: timer.phone })}</a>
      {:else}
        <p class="text-sm text-text-muted">{t('checkinAlertNoPhone', { member: timer.display_name })}</p>
      {/if}
      {#if liveState}
        <p class="text-sm text-text-muted">
          <i aria-hidden="true" class="las la-broadcast-tower mr-1"></i>{liveWords(liveState, liveReason, located)}
        </p>
        {#if liveState === 'unreachable'}
          <button class="btn btn-secondary btn-sm" onclick={tryAgain}>{t('appRetry')}</button>
        {/if}
      {/if}
    {:else}
      <p class="text-text-muted">{t('checkinLoading')}</p>
    {/if}
  </aside>
  <div class="flex-1 relative min-w-0">
    <div bind:this={mapEl} class="absolute inset-0"></div>
  </div>
</div>

<style>
  :global(.checkin-marker) {
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 9999px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 0.8rem;
    background: var(--theme-error);
    color: var(--theme-base);
    border: 3px solid var(--theme-base);
    box-shadow: var(--theme-shadow-md);
  }
</style>
